import { type ApiResponse } from 'utils/types';
import { STORAGE_KEYS, API_BASE_URL } from 'config';
import { getUserFacingErrorMessage, isNetworkError } from 'utils/apiError';
import { isTokenExpiringSoon } from 'utils/jwt';
import { retryWithBackoff } from 'utils/retry';

export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Customer data returned from login endpoint
 * Based on backend Customer model (excluding password_hash)
 */
export interface CustomerResponse {
  cd_id: number;
  cd_ow_account: string;
  cd_statement_name: string;
  cd_statement_email?: string;
  cd_statement_telephone?: string;
  cd_statement_address1?: string;
  cd_statement_address2?: string;
  cd_statement_address3?: string;
  cd_statement_town?: string;
  cd_statement_county?: string;
  cd_statement_country?: string;
  cd_statement_postcode?: string;
  // Invoice fields (used for authentication, not address)
  cd_invoice_name?: string;
  cd_invoice_email?: string;
  cd_invoice_telephone?: string;
  cd_vat_number?: string;
  cd_account_customer: boolean;
  credit_limit?: number;
  on_hold?: boolean;
  available_to_spend?: number;
  balance?: number;
  /** Currency of credit_limit / available_to_spend / balance — not always GBP. */
  credit_currency_code?: string;
  credit_currency_symbol?: string;
  password_reset_token?: string;
  password_reset_expires?: Date | string;
  last_login_at?: Date | string;
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface LoginResponse {
  customer: CustomerResponse;
  accessToken: string;
  refreshToken: string;
}

export interface AuthErrorResponse {
  success: false;
  error: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface GetCurrentUserResponse {
  user: CustomerResponse;
}

export interface RegisterRequest {
  email: string;
  password: string;
  companyName: string;
  contactName: string;
  contactPosition: string;
  phone?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  town?: string;
  county?: string;
  country: string;
  postcode: string;
}

export interface RegisterResponse {
  customer: CustomerResponse;
  token: string;
}

class AuthService {
  private baseUrl: string;
  private refreshPromise: Promise<RefreshTokenResponse> | null = null;
  private usePersistentStorage: boolean = true; // Default to localStorage

  constructor() {
    this.baseUrl = API_BASE_URL;
    // Check which storage is being used on initialization
    this.detectStorageType();
  }

  /**
   * Detect which storage type is being used based on existing tokens
   */
  private detectStorageType(): void {
    // Check localStorage first (persistent)
    const localToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    // Check sessionStorage (temporary)
    const sessionToken = sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    // If we have a token in localStorage, use persistent storage
    // If only in sessionStorage, use session storage
    // If neither, default to persistent (will be set on next login)
    if (localToken) {
      this.usePersistentStorage = true;
    } else if (sessionToken) {
      this.usePersistentStorage = false;
    }
  }

  /**
   * Get the appropriate storage object based on remember me preference
   */
  private getStorage(isPersistent: boolean = this.usePersistentStorage): Storage {
    return isPersistent ? localStorage : sessionStorage;
  }

  /**
   * Login user with email and password
   * @param rememberMe - If true, use localStorage (persistent) and longer token expiration. If false, use sessionStorage (session-only) and shorter token expiration.
   */
  async login(email: string, password: string, rememberMe: boolean = true): Promise<LoginResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/customers/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      const data: ApiResponse<LoginResponse> | AuthErrorResponse = await response.json();

      if (!response.ok || !data.success) {
        const errorMessage = 'error' in data ? data.error : 'Login failed. Please try again.';
        throw new Error(errorMessage);
      }

      if ('data' in data && data.success) {
        // Set storage type based on remember me preference
        this.usePersistentStorage = rememberMe;

        // Clear any existing tokens from the other storage type
        if (rememberMe) {
          // Switching to persistent storage - clear sessionStorage
          sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          sessionStorage.removeItem('refresh_token');
        } else {
          // Switching to session storage - clear localStorage
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem('refresh_token');
        }

        // Get the appropriate storage
        const storage = this.getStorage(rememberMe);

        // Store tokens in the selected storage
        storage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.data.accessToken);
        storage.setItem('refresh_token', data.data.refreshToken);

        // Note: User data is not stored in storage - it will be fetched from API when needed

        return data.data;
      }

      throw new Error('Invalid response from server');
    } catch (error) {
      if (error instanceof Error && !isNetworkError(error)) {
        throw error;
      }

      throw new Error(getUserFacingErrorMessage(error, 'Login failed. Please try again.'));
    }
  }

  /**
   * Register new user
   * @param userData - Registration data
   * @param rememberMe - If true, use localStorage (persistent). If false, use sessionStorage (session-only).
   */
  async register(userData: RegisterRequest, rememberMe: boolean = true): Promise<RegisterResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/customers/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    const data: ApiResponse<RegisterResponse> | AuthErrorResponse = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data ? data.error : 'Registration failed. Please try again.';
      throw new Error(errorMessage);
    }

    if ('data' in data && data.success) {
      // Set storage type based on remember me preference
      this.usePersistentStorage = rememberMe;

      // Clear any existing tokens from the other storage type
      if (rememberMe) {
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        sessionStorage.removeItem('refresh_token');
      } else {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem('refresh_token');
      }

      // Get the appropriate storage
      const storage = this.getStorage(rememberMe);

      // Store token (note: register endpoint returns 'token', not 'accessToken')
      storage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.data.token);

      // Note: Register endpoint doesn't return refreshToken, so we'll need to handle that
      // For now, we'll just store the token. The user can refresh later if needed.

      return data.data;
    }

    throw new Error('Invalid response from server');
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<RefreshTokenResponse> {
    // If there's already a refresh in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Create a new refresh promise with retry logic
    this.refreshPromise = retryWithBackoff(
      async () => {
        const response = await fetch(`${this.baseUrl}/api/v1/customers/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });

        const data: ApiResponse<RefreshTokenResponse> | AuthErrorResponse = await response.json();

        if (!response.ok || !data.success) {
          const errorMessage = 'error' in data ? data.error : 'Token refresh failed';
          this.clearAuthData();
          throw new Error(errorMessage);
        }

        if ('data' in data && data.success) {
          // Store new tokens in the same storage type being used
          const storage = this.getStorage();
          storage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.data.accessToken);
          storage.setItem('refresh_token', data.data.refreshToken);

          return data.data;
        }

        throw new Error('Invalid response from server');
      },
      {
        maxRetries: 2, // Fewer retries for auth
        initialDelay: 500,
        shouldRetry: (error) => {
          // Only retry on network errors, not auth errors
          return error instanceof TypeError;
        },
      }
    ).finally(() => {
      // Clear the promise so we can refresh again if needed
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  /**
   * Get access token, refreshing if needed
   * This should be called before making authenticated API requests
   */
  async getValidAccessToken(): Promise<string | null> {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return null;
    }

    // Check if token needs refresh (expiring soon or expired)
    if (isTokenExpiringSoon(accessToken)) {
      try {
        const refreshed = await this.refreshAccessToken();
        return refreshed.accessToken;
      } catch (error) {
        // If refresh fails, clear auth data and return null
        this.clearAuthData();
        return null;
      }
    }

    return accessToken;
  }

  /**
   * Get stored access token (without refresh check)
   * Checks both localStorage and sessionStorage
   */
  getAccessToken(): string | null {
    // Check current storage type first
    const currentStorage = this.getStorage();
    const token = currentStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    if (token) {
      return token;
    }

    // If not found in current storage, check the other one
    const otherStorage = this.getStorage(!this.usePersistentStorage);
    const otherToken = otherStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    if (otherToken) {
      // Found token in other storage - switch storage type
      this.usePersistentStorage = !this.usePersistentStorage;
      return otherToken;
    }

    return null;
  }

  /**
   * Get stored refresh token
   * Checks both localStorage and sessionStorage
   */
  getRefreshToken(): string | null {
    const storage = this.getStorage();
    const token = storage.getItem('refresh_token');

    if (token) {
      return token;
    }

    // Check the other storage
    const otherStorage = this.getStorage(!this.usePersistentStorage);
    const otherToken = otherStorage.getItem('refresh_token');

    if (otherToken) {
      // Found token in other storage - switch storage type
      this.usePersistentStorage = !this.usePersistentStorage;
      return otherToken;
    }

    return null;
  }

  /**
   * Get current user profile from API
   * Requires a valid access token
   */
  async getCurrentUser(): Promise<CustomerResponse> {
    return retryWithBackoff(
      async () => {
        const accessToken = await this.getValidAccessToken();

        if (!accessToken) {
          throw new Error('No access token available');
        }

        const response = await fetch(`${this.baseUrl}/api/v1/customers/profile`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data: ApiResponse<GetCurrentUserResponse> | AuthErrorResponse = await response.json();

        if (!response.ok || !data.success) {
          const errorMessage = 'error' in data ? data.error : 'Failed to fetch user profile';

          // If unauthorized, clear auth data
          if (response.status === 401) {
            this.clearAuthData();
          }

          throw new Error(errorMessage);
        }

        if ('data' in data && data.success) {
          return data.data.user;
        }

        throw new Error('Invalid response from server');
      },
      {
        maxRetries: 2,
        initialDelay: 500,
        shouldRetry: (error) => {
          // Only retry on network errors, not 4xx errors (especially 401)
          return error instanceof TypeError;
        },
      }
    );
  }

  /**
   * Update current user profile
   */
  async updateCurrentUser(updates: {
    address1?: string;
    address2?: string;
    address3?: string;
    town?: string;
    county?: string;
    country?: string;
    postcode?: string;
    phone?: string;
    companyName?: string;
  }): Promise<CustomerResponse> {
    const accessToken = await this.getValidAccessToken();

    if (!accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${this.baseUrl}/api/v1/customers/profile`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    const data: ApiResponse<GetCurrentUserResponse> | AuthErrorResponse = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data ? data.error : 'Failed to update profile';

      if (response.status === 401) {
        this.clearAuthData();
      }

      throw new Error(errorMessage);
    }

    if ('data' in data && data.success) {
      return data.data.user;
    }

    throw new Error('Invalid response from server');
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const accessToken = await this.getValidAccessToken();

    if (!accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch(`${this.baseUrl}/api/v1/customers/change-password`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data: ApiResponse<{ message: string }> | AuthErrorResponse = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data ? data.error : 'Failed to change password';
      throw new Error(errorMessage);
    }

    if ('data' in data && data.success) {
      return data.data;
    }

    throw new Error('Invalid response from server');
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/customers/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data: ApiResponse<{ message: string }> | AuthErrorResponse = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data ? data.error : 'Failed to send password reset email';
      throw new Error(errorMessage);
    }

    if ('data' in data && data.success) {
      return data.data;
    }

    throw new Error('Invalid response from server');
  }

  /**
   * Reset password using reset token
   */
  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/customers/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password }),
    });

    const data: ApiResponse<{ message: string }> | AuthErrorResponse = await response.json();

    if (!response.ok || !data.success) {
      const errorMessage = 'error' in data ? data.error : 'Failed to reset password';
      throw new Error(errorMessage);
    }

    if ('data' in data && data.success) {
      return data.data;
    }

    throw new Error('Invalid response from server');
  }

  /**
   * Clear all auth data from both storage types
   */
  clearAuthData(): void {
    // Clear from localStorage
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem('refresh_token');

    // Clear from sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem('refresh_token');
  }
}

export const authService = new AuthService();
